import { SignedIn, SignedOut, SignIn } from '@clerk/clerk-react';
import { Route, Routes } from 'react-router';
import { Box, CircularProgress } from '@mui/material';
import { NotificationsProvider } from '@toolpad/core/useNotifications';
import ReactQueryProvider from '@/providers/react-query';
import DatePickerLocalizationProvider from '@/providers/date-picker-localization';
import ThemeModesProvider from '@/providers/theme-provider';
import LowContrastThemeGuard from '@/providers/low-contrast-theme-guard';
import ThemeProvider from '@/providers/theme';
import AuthenticationProvider from '@/providers/authentication';
import SyncBrowserRouter from '@/providers/sync-browser-router';
import LocalizedThemeProvider from './providers/localized-theme';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/600.css';
import '@fontsource/roboto/700.css';
import NavBar from './components/nav-bar/nav-bar';
import OnboardingContent from './components/onboarding-content/onboarding-content';
import { SyncUserActiveOrganization } from './components/organizations/sync-user-active-organization';
import { useFeatureAccess } from './hooks/use-feature-access';
import { useGetUserQuery } from './state/queries/users/use-get-user';
import { useMobileFocusScrollIntoView } from './hooks/use-mobile-focus-scroll-into-view';
import AcceptInvitationPage from '@/components/invitation-flow/accept-invitation-page';
import InvitedToEntityWithSearchParam from '@/components/invitation-flow/invited-to-entity.modal';
import LegalPage from '@/components/legal/legal-page';
import LegalHeader from '@/components/legal/legal-header';
import LegalLink from '@/components/legal/legal-link';
import EmailOptOutPage from '@/components/email-preferences/email-opt-out-page';

function SignedInContent() {
  const { data: user, isLoading: userLoading } = useGetUserQuery();
  // `onboarding` feature = extended flow (questionnaire + optional org). Basic onboarding always runs in OnboardingContent.
  const { hasAccess: extendedOnboarding, isLoading: extendedOnboardingLoading } = useFeatureAccess('onboarding');

  if (userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const needsOnboarding = user != null && !(user.isOnboarded ?? false);
  if (needsOnboarding && extendedOnboardingLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return <OnboardingContent extendedOnboarding={extendedOnboarding} />;
}

function MainApp() {
  return (
    <>
      <SignedIn>
        <SyncUserActiveOrganization>
          <InvitedToEntityWithSearchParam />
          <NavBar />
          <SignedInContent />
        </SyncUserActiveOrganization>
      </SignedIn>
      <SignedOut>
        <LegalHeader />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <SignIn />
        </Box>
      </SignedOut>
      <SignedOut>
        <LegalLink />
      </SignedOut>
    </>
  );
}

export default function App() {
  useMobileFocusScrollIntoView();

  return (
    <ThemeModesProvider>
      <ThemeProvider>
        <LocalizedThemeProvider>
          <SyncBrowserRouter>
            <AuthenticationProvider>
              <ReactQueryProvider>
                <LowContrastThemeGuard />
                <NotificationsProvider>
                  <DatePickerLocalizationProvider>
                    <Box
                      sx={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    ></Box>
                    <Routes>
                      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
                      <Route path="/legal" element={<LegalPage />} />
                      <Route path="/legal/:slug" element={<LegalPage />} />
                      <Route path="/email-preferences/opt-out" element={<EmailOptOutPage />} />
                      <Route path="/email-preferences/opt-in" element={<EmailOptOutPage />} />
                      <Route path="*" element={<MainApp />} />
                    </Routes>
                  </DatePickerLocalizationProvider>
                </NotificationsProvider>
              </ReactQueryProvider>
            </AuthenticationProvider>
          </SyncBrowserRouter>
        </LocalizedThemeProvider>
      </ThemeProvider>
    </ThemeModesProvider>
  );
}
